import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, BookOpen, GraduationCap, UserPlus, Users, Wallet } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { fmtDate } from "@/components/admin/DataTable";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <Icon className="h-5 w-5 text-[#222d99]" strokeWidth={1.5} aria-hidden="true" />
    <p className={`mt-4 font-display ${value === null ? "text-base font-semibold text-slate-600" : "text-3xl font-extrabold text-slate-900"}`} data-testid={testid}>{value === null ? "No data yet" : value}</p>
    <p className="text-xs text-slate-600">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const api = useApi();
  const { me } = useMe();
  const perms = me.user.permissions;
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!perms.includes("dashboard.view")) return;
    if (range === "custom" && (!custom.start || !custom.end)) return;
    api.get("/admin/dashboard", { params: { range, ...(range === "custom" ? custom : {}) } }).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(errMsg(e)));
  }, [api, range, custom, perms]);

  if (!perms.includes("dashboard.view")) return (
    <div><PageHeader eyebrow="Admin Control Centre" title={`Welcome, ${me.user.name}`} />
      <p className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700" data-testid="no-modules">No admin modules are assigned to your role ({me.user.role_name}). A Super Admin can change your permissions.</p></div>
  );
  const pend = d ? d.pending.employers + d.pending.courses_in_review : 0;
  return (
    <div>
      <PageHeader eyebrow="Admin Control Centre" title="Dashboard">
        <Tabs value={range} onValueChange={setRange}>
          <TabsList>{[["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["custom", "Custom"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`range-${v}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
      </PageHeader>
      {range === "custom" && (
        <div className="mb-6 flex flex-wrap gap-3">
          <label className="text-sm text-slate-700">From <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid="range-start" /></label>
          <label className="text-sm text-slate-700">To <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid="range-end" /></label>
        </div>
      )}
      {err && <p className="mb-4 text-rose-700">{err}</p>}
      {!d ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Kpi icon={Users} label="Active users" value={d.active_users} testid="kpi-active-users" />
            <Kpi icon={UserPlus} label="New registrations" value={d.new_registrations} testid="kpi-registrations" />
            <Kpi icon={BookOpen} label="Course enrolments" value={d.enrolments} testid="kpi-enrolments" />
            <Kpi icon={Wallet} label="Revenue" value={d.revenue} testid="kpi-revenue" />
            <Kpi icon={GraduationCap} label="Completion rate" value={d.completion_rate === null ? null : `${d.completion_rate}%`} testid="kpi-completion" />
            <Kpi icon={BadgeCheck} label="Pending approvals" value={pend} testid="kpi-pending" />
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="alerts-panel">
              <h2 className="font-semibold text-slate-900">Alerts</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {d.alerts.map((a) => <li key={a} className="flex gap-2 text-amber-900"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{a}</li>)}
                {!d.alerts.length && <li className="text-slate-600">No alerts</li>}
              </ul>
            </section>
            <section className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1" data-testid="activity-panel">
              <h2 className="font-semibold text-slate-900">Recent activity</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {d.activity.map((a) => <li key={a.id}><span className="font-medium text-slate-900">{a.action}</span> <span className="text-slate-600">by {a.actor_email} · {fmtDate(a.created_at)}</span></li>)}
                {!d.activity.length && <li className="text-slate-600">No data yet</li>}
              </ul>
            </section>
            <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="quick-actions">
              <h2 className="font-semibold text-slate-900">Quick actions</h2>
              <div className="mt-3 flex flex-col gap-2">
                {perms.includes("courses.edit") && <Button asChild variant="outline" data-testid="qa-add-course"><Link to="/admin/courses">Add course</Link></Button>}
                {perms.includes("users.invite") && <Button asChild variant="outline" data-testid="qa-invite"><Link to="/admin/users?invite=1">Invite user</Link></Button>}
                {perms.includes("employers.approve") && <Button asChild variant="outline" data-testid="qa-approvals"><Link to="/admin/employers">Review approvals</Link></Button>}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
